import { clsx } from 'clsx';
import type { RefObject } from 'preact';

import http from '~/shared/http';
import Pagination from '../Pagination/Pagination';
import Ad from '../Ad/Ad';
import dayjs from 'dayjs';
import inView from '~/utils/in-view';
import { getProgress } from '../../player/store';
import type { PlayerStore } from '../../player/store';
import { useEffect, useRef, useState } from 'preact/hooks';
import { useDebouncedState } from '~/shared/hooks/use-debounced-state';
import { PauseIcon, PlayIcon } from '~/components/Player/icons';
import type { Podcast } from '@/types/podcast';

function ChevronIcon() {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
            <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" fill="#aaaaaa" />
            <path d="M0 0h24v24H0z" fill="none" />
        </svg>
    );
}

function AdSecondaryAndOutstream() {
    const [renderCount, setRenderCount] = useState(0);
    useEffect(() => {
        setRenderCount((prev) => prev + 1);
    }, []);

    return (
        <>
            <Ad display="mobile" type={renderCount === 0 ? 'outstream' : 'secondary'} />
            <Ad display="desktop" type={renderCount === 0 ? 'outstream' : 'secondary'} />
        </>
    );
}

const scrollEpisodesIntoView = <T extends HTMLElement>(ref: RefObject<T>) => {
    ref.current?.scrollIntoView();
    window.scrollTo(0, window.scrollY - 66);
};

const formatDate = (date: string) =>
    dayjs(date.replace(/(\d{2,4}-\d{1,2}-\d{1,2}).*/, '$1')).format('YYYY-MM-DD');

type EpisodeProps = {
    store: PlayerStore;
    podcast: Podcast;
    title: string;
    header: string;
    podcastUrl: string;
};

type UrlFilterState = {
    page: number;
    order: 'asc' | 'desc';
    q: string;
    from: string;
    to: string;
};

function readUrlParams(): UrlFilterState {
    const params = new URLSearchParams(window.location.search);
    const page = Math.max(parseInt(params.get('page') || '1', 10) || 1, 1);
    const order = params.get('order') === 'asc' ? 'asc' : 'desc';
    const q = params.get('q') || '';
    const dateRe = /^\d{4}-\d{2}-\d{2}$/;
    const rawFrom = params.get('from') || '';
    const rawTo = params.get('to') || '';
    const from = dateRe.test(rawFrom) ? rawFrom : '';
    const to = dateRe.test(rawTo) ? rawTo : '';
    return { page, order, q, from, to };
}

function buildQueryString(state: UrlFilterState): string {
    const params = new URLSearchParams();

    if (state.page > 1) params.set('page', String(state.page));
    if (state.order === 'asc') params.set('order', 'asc');
    if (state.q) params.set('q', state.q);
    if (state.from) params.set('from', state.from);
    if (state.to) params.set('to', state.to);

    const qs = params.toString();
    return qs ? `${window.location.pathname}?${qs}` : window.location.pathname;
}

function updateUrlParams(state: UrlFilterState) {
    history.replaceState(null, '', buildQueryString(state));
}

async function fetchData(
    podcastId: string,
    limit: number,
    offset: number,
    order: 'asc' | 'desc',
    q: string,
    from: string,
    to: string,
    signal?: AbortSignal,
) {
    const params = new URLSearchParams({
        limit: String(limit),
        offset: String(offset),
        order,
    });
    if (q) params.set('q', q);
    if (from) params.set('from', from);
    if (to) params.set('to', to);

    const { data } = await http.get(`/api/podcasts/${podcastId}/episodes?${params}`, { signal });

    return { episodes: data.items, pagination: data.pagination };
}

function useEpisodeQuery({
    podcastId,
    limit,
    offset,
    order = 'desc',
    q = '',
    from = '',
    to = '',
    shouldFetch,
}: {
    shouldFetch: boolean;
    podcastId: string;
    limit: number;
    order: 'asc' | 'desc';
    offset: number;
    q: string;
    from: string;
    to: string;
}) {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [data, setData] = useState<{ episodes: any[]; pagination?: { total?: number } }>({
        episodes: [],
        pagination: undefined,
    });
    const abortRef = useRef<AbortController | null>(null);

    useEffect(() => {
        if (!shouldFetch) {
            abortRef.current?.abort();
            setData({ episodes: [], pagination: undefined });
            setLoading(false);
            setError(null);
            return;
        }
        abortRef.current?.abort();
        const controller = new AbortController();
        abortRef.current = controller;
        setLoading(true);
        setError(null);
        fetchData(podcastId, limit, offset, order, q, from, to, controller.signal)
            .then((data) => {
                if (controller.signal.aborted) return;
                setData(data);
                setLoading(false);
            })
            .catch((error: any) => {
                if (controller.signal.aborted) return;
                console.error('Failed to fetch episodes:', error);
                setError('Kunde inte ladda avsnitt');
                setLoading(false);
            });
        return () => controller.abort();
    }, [shouldFetch, podcastId, limit, offset, order, q, from, to]);

    return { loading, error, data };
}

let filterPanelUid = 0;

interface DateChipProps {
    label: string;
    value: string;
    onChange: (v: string) => void;
    fullWidth?: boolean;
}

export function openDatePicker(input: HTMLInputElement | null): void {
    if (!input) {
        return;
    }

    input.focus();

    if (typeof input.showPicker === 'function') {
        input.showPicker();
        return;
    }

    input.click();
}

function DateChip({ label, value, onChange, fullWidth = false }: DateChipProps): JSX.Element {
    const inputRef = useRef<HTMLInputElement>(null);

    return (
        <span class={clsx('relative', fullWidth ? 'flex' : 'inline-flex')}>
            <button
                type="button"
                class={clsx(
                    'inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border transition-colors cursor-pointer',
                    fullWidth && 'w-full justify-center',
                    value
                        ? 'border-brand-600 text-brand-600 bg-brand-600/5'
                        : 'border-[#D6D6D6] text-gray-600 hover:border-gray-400',
                )}
                onClick={() => openDatePicker(inputRef.current)}
            >
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                >
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                    <line x1="16" y1="2" x2="16" y2="6" />
                    <line x1="8" y1="2" x2="8" y2="6" />
                    <line x1="3" y1="10" x2="21" y2="10" />
                </svg>
                {value || label}
            </button>
            <input
                ref={inputRef}
                type="date"
                class="pointer-events-none absolute inset-0 opacity-0"
                tabIndex={-1}
                aria-hidden="true"
                value={value}
                onChange={(e) => onChange((e.target as HTMLInputElement).value)}
            />
        </span>
    );
}

function FilterIcon() {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
        >
            <path
                d="M4 7H20M7 12H17M10 17H14"
                stroke="currentColor"
                stroke-width="1.8"
                stroke-linecap="round"
            />
        </svg>
    );
}

function SortIcon() {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 20 20"
            fill="currentColor"
        >
            <path
                fillRule="evenodd"
                d="M2.24 6.8a.75.75 0 001.06-.04l1.95-2.1v8.59a.75.75 0 001.5 0V4.66l1.95 2.1a.75.75 0 101.1-1.02l-3.25-3.5a.75.75 0 00-1.1 0L2.2 5.74a.75.75 0 00.04 1.06zm8 6.4a.75.75 0 00-.04 1.06l3.25 3.5a.75.75 0 001.1 0l3.25-3.5a.75.75 0 10-1.1-1.02l-1.95 2.1V6.75a.75.75 0 00-1.5 0v8.59l-1.95-2.1a.75.75 0 00-1.06-.04z"
                clipRule="evenodd"
            />
        </svg>
    );
}

export function getActiveEpisodeFilterCount(
    order: 'asc' | 'desc',
    fromDate: string,
    toDate: string,
): number {
    return Number(order === 'asc') + Number(Boolean(fromDate)) + Number(Boolean(toDate));
}

export function shouldFetchEpisodes({
    page,
    order,
    searchQuery,
    fromDate,
    toDate,
    initialLimit,
}: {
    page: number;
    order: 'asc' | 'desc';
    searchQuery: string;
    fromDate: string;
    toDate: string;
    initialLimit?: number;
}): boolean {
    return (
        page > 1 ||
        order !== 'desc' ||
        Boolean(searchQuery || fromDate || toDate) ||
        initialLimit !== perPage
    );
}

export const episodeSearchInputClass =
    'w-full sm:max-w-sm sm:flex-none min-w-0 flex-1 border border-[#D6D6D6] rounded-full px-4 py-2 text-sm';
export const episodeToolbarClass = 'flex items-center gap-2 sm:justify-center';

interface EpisodeFilterControlsProps {
    open: boolean;
    order: 'asc' | 'desc';
    fromDate: string;
    toDate: string;
    panelId: string;
    onTogglePanel: () => void;
    onToggleOrder: () => void;
    onFromDateChange: (value: string) => void;
    onToDateChange: (value: string) => void;
    onReset: () => void;
}

export function EpisodeFilterControls({
    open,
    order,
    fromDate,
    toDate,
    panelId,
    onTogglePanel,
    onToggleOrder,
    onFromDateChange,
    onToDateChange,
    onReset,
}: EpisodeFilterControlsProps): JSX.Element {
    const activeFilterCount = getActiveEpisodeFilterCount(order, fromDate, toDate);
    const hasActiveFilters = activeFilterCount > 0;

    return (
        <div class="relative shrink-0">
            <button
                type="button"
                aria-label={
                    hasActiveFilters ? `Visa filter, ${activeFilterCount} aktiva` : 'Visa filter'
                }
                aria-controls={panelId}
                aria-expanded={open}
                title="Visa filter"
                class={clsx(
                    'relative inline-flex h-10 w-10 items-center justify-center rounded-full border transition-colors',
                    hasActiveFilters || open
                        ? 'border-brand-600 bg-brand-600/5 text-brand-600'
                        : 'border-[#D6D6D6] text-gray-600 hover:border-gray-400',
                )}
                onClick={onTogglePanel}
            >
                <FilterIcon />
                {hasActiveFilters && (
                    <span class="absolute -right-1 -top-1 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-brand-600 px-1 text-[10px] font-semibold text-white">
                        {activeFilterCount}
                    </span>
                )}
            </button>

            {open && (
                <div
                    id={panelId}
                    role="dialog"
                    aria-label="Filtrera avsnitt"
                    class="absolute right-0 top-full z-20 mt-2 w-[min(22rem,calc(100vw-2rem))] max-w-[calc(100vw-2rem)] rounded-2xl border border-[#D6D6D6] bg-white p-3 shadow-lg"
                >
                    <div class="flex items-center justify-between gap-3">
                        <p class="text-[11px] font-semibold uppercase tracking-[0.2em] text-gray-500">
                            Filter
                        </p>
                        {hasActiveFilters && (
                            <button
                                type="button"
                                class="text-xs font-medium text-gray-500 transition-colors hover:text-gray-700"
                                onClick={onReset}
                            >
                                Återställ
                            </button>
                        )}
                    </div>

                    <div class="mt-3 flex flex-col gap-2">
                        <button
                            type="button"
                            class="inline-flex items-center justify-between gap-3 rounded-full border border-[#D6D6D6] px-3 py-2 text-xs text-gray-600 transition-colors hover:border-gray-400"
                            onClick={onToggleOrder}
                        >
                            <span class="inline-flex items-center gap-2">
                                <SortIcon />
                                Sortering
                            </span>
                            <span>{order === 'desc' ? 'Senaste' : 'Äldsta'}</span>
                        </button>

                        <div class="grid grid-cols-2 gap-2">
                            <DateChip
                                label="Från"
                                value={fromDate}
                                onChange={onFromDateChange}
                                fullWidth
                            />
                            <DateChip
                                label="Till"
                                value={toDate}
                                onChange={onToDateChange}
                                fullWidth
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

const perPage = 10;
export default function Episodes(props: EpisodeProps) {
    const [initial] = useState(readUrlParams);
    const [compInView, setInView] = useState(false);
    const [order, setOrder] = useState<'desc' | 'asc'>(initial.order);
    const [page, setPage] = useState<number>(initial.page);
    const [searchInput, setSearchInput] = useState(initial.q);
    const [searchQuery, setSearchQuery, setSearchQueryImmediate] = useDebouncedState(
        initial.q,
        350,
    );
    const [fromDate, setFromDate] = useState(initial.from);
    const [toDate, setToDate] = useState(initial.to);
    const [filtersOpen, setFiltersOpen] = useState(false);
    const filterPanelRef = useRef<HTMLDivElement>(null);
    const filterPanelIdRef = useRef(`episode-filters-${++filterPanelUid}`);
    const searchInputRef = useRef<HTMLInputElement>(null);

    const hasFilters = Boolean(searchQuery || fromDate || toDate);
    const shouldFetch = shouldFetchEpisodes({
        page,
        order,
        searchQuery,
        fromDate,
        toDate,
        initialLimit: props.podcast.meta.pagination?.limit,
    });

    const { loading, error, data } = useEpisodeQuery({
        podcastId: props.podcast.itunes_id,
        limit: perPage,
        offset: (page - 1) * perPage,
        order,
        q: searchQuery,
        from: fromDate,
        to: toDate,
        shouldFetch,
    });

    useEffect(() => {
        updateUrlParams({ page, order, q: searchQuery, from: fromDate, to: toDate });
    }, [page, order, searchQuery, fromDate, toDate]);

    const shouldShowInitialEpisodes = page === 1 && order === 'desc' && !hasFilters;
    const episodes =
        data.pagination === undefined && shouldShowInitialEpisodes
            ? props.podcast.meta.episodes
            : data.episodes;

    // Clamp page to valid range once real total arrives
    useEffect(() => {
        if (data.pagination?.total !== undefined) {
            const maxPage = Math.max(1, Math.ceil(data.pagination.total / perPage));
            if (page > maxPage) {
                setPage(maxPage);
            }
        }
    }, [data.pagination?.total]);

    const player = props.store;

    useEffect(() => {
        inView('.items-grid').once('enter', () => {
            setInView(true);
        });
    }, []);

    useEffect(() => {
        const el = searchInputRef.current;
        if (!el) return;
        const onClear = () => {
            if (!el.value) {
                setSearchInput('');
                setSearchQueryImmediate('');
                setPage(1);
            }
        };
        el.addEventListener('search', onClear);
        return () => el.removeEventListener('search', onClear);
    }, []);

    const episodesListRef = useRef(null);
    useEffect(() => {
        if (!loading && episodesListRef.current && page > 1) {
            scrollEpisodesIntoView(episodesListRef);
        }
    }, [loading]);

    useEffect(() => {
        if (!filtersOpen) {
            return;
        }

        const handleClickOutside = (event: MouseEvent) => {
            if (
                event.target instanceof Node &&
                filterPanelRef.current &&
                !filterPanelRef.current.contains(event.target)
            ) {
                setFiltersOpen(false);
            }
        };

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setFiltersOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [filtersOpen]);

    return (
        <>
            <aside
                class={clsx('episodes items-grid items-grid--will-reveal', {
                    'items-grid--in-view': compInView,
                })}
            >
                <div class="flex justify-center">
                    <h2 ref={episodesListRef} class="medium-title flex-1">
                        {props.header} {props.title}
                    </h2>
                </div>

                <div class="mt-3 border-b border-[#D6D6D6] pb-3">
                    <div ref={filterPanelRef} class={episodeToolbarClass}>
                        <input
                            type="search"
                            class={episodeSearchInputClass}
                            placeholder="Sök bland avsnitt..."
                            value={searchInput}
                            ref={searchInputRef}
                            onInput={(e) => {
                                const val = (e.target as HTMLInputElement).value;
                                setSearchInput(val);
                                val ? setSearchQuery(val) : setSearchQueryImmediate('');
                                setPage(1);
                            }}
                        />
                        <EpisodeFilterControls
                            open={filtersOpen}
                            order={order}
                            fromDate={fromDate}
                            toDate={toDate}
                            panelId={filterPanelIdRef.current}
                            onTogglePanel={() => setFiltersOpen((value) => !value)}
                            onToggleOrder={() => {
                                setOrder(order === 'desc' ? 'asc' : 'desc');
                                setPage(1);
                            }}
                            onFromDateChange={(val) => {
                                setFromDate(val);
                                setPage(1);
                            }}
                            onToDateChange={(val) => {
                                setToDate(val);
                                setPage(1);
                            }}
                            onReset={() => {
                                setOrder('desc');
                                setFromDate('');
                                setToDate('');
                                setPage(1);
                            }}
                        />
                    </div>
                </div>

                {loading ? (
                    <div class="spinner absolute top-1/4 left-1/2 -translate-x-1/2">
                        <div class="bounce1" />
                        <div class="bounce2" />
                        <div class="bounce3" />
                    </div>
                ) : null}
                {!loading && error && <p class="text-sm text-red-500 py-4 text-center">{error}</p>}
                {!loading && !error && hasFilters && episodes.length === 0 && (
                    <p class="text-sm text-gray-500 py-4 text-center">Inga avsnitt hittades</p>
                )}
                <div class={clsx({ 'episodes_fade-out': loading }, 'episodes__list episodes_fade')}>
                    {episodes.map((episode, index) => (
                        <div key={episode.slug || episode.media || index}>
                            {(index + 1) % 7 === 0 && episodes.length > index + 2 ? (
                                <AdSecondaryAndOutstream />
                            ) : null}

                            <div class="episodes__list__item episode--is-playing">
                                <button
                                    onClick={() => {
                                        const playing =
                                            player.data.playing.value &&
                                            player.data.episode.value?.media === episode.media;
                                        playing
                                            ? player.pause()
                                            : player.play(
                                                  episode,
                                                  props.podcast,
                                                  getProgress(episode, props.podcast.itunes_id)
                                                      .currentSeek,
                                              );
                                    }}
                                    title={
                                        player.data.playing.value &&
                                        player.data.episode.value?.media === episode.media
                                            ? 'Pausa avsnittet'
                                            : 'Spela avsnittet'
                                    }
                                    type="button"
                                    class="episodes__list__playbutton fill-black"
                                >
                                    {player.data.playing.value &&
                                    player.data.episode.value?.media === episode.media ? (
                                        <PauseIcon />
                                    ) : (
                                        <PlayIcon />
                                    )}
                                </button>

                                <a
                                    href={getEpisodeUrl(episode, props.podcastUrl)}
                                    title={`Avsnitt: ${episode.title}`}
                                >
                                    <div class="episodes__list__item__top">
                                        <h3 class="episodes__list__title">{episode.title}</h3>
                                        <div class="episodes__list__item__meta">
                                            <time>{formatDate(episode.published)}</time>
                                            {episode.duration !== null ? (
                                                <time>&#124; {episode.duration}</time>
                                            ) : null}
                                        </div>
                                        <div class="episodes__list__fold">
                                            <ChevronIcon />
                                        </div>
                                    </div>
                                    <span
                                        class="episodes_progress"
                                        style={{
                                            width: `${episodeProgress(
                                                episode,
                                                props.podcast.itunes_id,
                                                player,
                                            )}%`,
                                        }}
                                    />
                                </a>
                            </div>
                        </div>
                    ))}
                </div>
            </aside>
            <Pagination
                pagesNeighbour={2}
                pages={Math.max(
                    1,
                    Math.ceil(
                        (data.pagination?.total ?? props.podcast.meta.pagination?.total ?? 0) /
                            perPage,
                    ),
                )}
                currentPage={page}
                buildHref={(p) =>
                    buildQueryString({ page: p, order, q: searchQuery, from: fromDate, to: toDate })
                }
                goTo={setPage}
            />
        </>
    );
}

const getEpisodeUrl = (episode: { slug: string }, podcastUrl: string) =>
    `${podcastUrl}/${episode.slug}`;

const episodeProgress = (
    episode: { media: string },
    podcastId: string,
    playerStore: PlayerStore,
) => {
    if (playerStore.data.playing.value && episode.media === playerStore.data.episode.value?.media) {
        return playerStore.progress;
    }
    return getProgress(episode, podcastId).progress;
};
